import { createContext, useContext, ReactNode, useState, useEffect } from "react";
import { User } from "@shared/schema";
import { queryClient } from "./queryClient";
import { apiRequest } from "./queryClient";

interface AppContextType {
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  user: User | null;
  setUser: (user: User | null) => void;
  token?: string | null;  // Token JWT opzionale (null o undefined entrambi validi)
  setToken?: (token: string | null) => void;  // Funzione per impostare il token
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  isAnyProfileSheetOpen: boolean;
  setIsAnyProfileSheetOpen: (open: boolean) => void;
  isEventSheetOpen: boolean;
  setIsEventSheetOpen: (open: boolean) => void;
  eventEditMode: boolean;
  setEventEditMode: (editMode: boolean) => void;
  eventEditModeToggle: (() => void) | null;
  setEventEditModeToggle: (toggle: (() => void) | null) => void;
  canEditCurrentEvent: boolean;
  setCanEditCurrentEvent: (canEdit: boolean) => void;
  // Profile edit mode
  profileEditMode: boolean;
  setProfileEditMode: (editMode: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({
  children,
  value
}: {
  children: ReactNode;
  value: AppContextType;
}) {
  const [isAnyProfileSheetOpen, setIsAnyProfileSheetOpen] = useState(false);
  const [isEventSheetOpen, setIsEventSheetOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(value.isLoading || false);
  const [eventEditMode, setEventEditMode] = useState(false);
  const [eventEditModeToggle, setEventEditModeToggle] = useState<(() => void) | null>(null);
  const [canEditCurrentEvent, setCanEditCurrentEvent] = useState(false);
  const [profileEditMode, setProfileEditMode] = useState(false);

  const contextValue = {
    ...value,
    isAnyProfileSheetOpen,
    setIsAnyProfileSheetOpen,
    isEventSheetOpen,
    setIsEventSheetOpen,
    isLoading,
    setIsLoading,
    eventEditMode,
    setEventEditMode,
    eventEditModeToggle,
    setEventEditModeToggle,
    canEditCurrentEvent,
    setCanEditCurrentEvent,
    profileEditMode,
    setProfileEditMode,
  };

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    console.error("useApp must be used within an AppProvider - this should not happen in production");
    // Instead of throwing, return a default context to prevent crashes
    return {
      isAuthenticated: false,
      setIsAuthenticated: () => {},
      user: null,
      setUser: () => {},
      token: null,
      setToken: () => {},
      isLoading: false,
      setIsLoading: () => {},
      isAnyProfileSheetOpen: false,
      setIsAnyProfileSheetOpen: () => {},
      isEventSheetOpen: false,
      setIsEventSheetOpen: () => {},
      eventEditMode: false,
      setEventEditMode: () => {},
      eventEditModeToggle: null,
      setEventEditModeToggle: () => {},
      canEditCurrentEvent: false,
      setCanEditCurrentEvent: () => {},
      profileEditMode: false,
      setProfileEditMode: () => {},
    };
  }
  return context;
}
